// Shared dark top bar used by the Designer / DTF / Profile pages. The right-
// hand action set matches the public home page so navigation feels uniform
// no matter where the customer is:
//   logged out         -> [ Sign in ] [ Sign up ]
//   logged in          -> [ Profile ] [ Sign out ]
//   logged in (admin)  -> [ Admin ] [ Profile ] [ Sign out ]
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'

interface MeShape {
  authenticated: boolean
  user?: { id: string; email: string; firstName: string | null; lastName: string | null; role: string } | null
}

export default function TopBar({ title, right }: { title?: string; right?: React.ReactNode }) {
  const { user: authkitUser, signIn, signOut } = useAuth()
  // Mirror /api/me so we know the role (admin/manager). AuthKit React doesn't
  // expose role — the source of truth lives on the server.
  const [serverRole, setServerRole] = useState<string | null>(null)
  useEffect(() => {
    let mounted = true
    fetch('/api/me', { credentials: 'include' })
      .then((r) => r.json())
      .then((d: MeShape) => { if (mounted) setServerRole(d?.authenticated ? d.user?.role ?? 'customer' : null) })
      .catch(() => { if (mounted) setServerRole(null) })
    return () => { mounted = false }
  }, [authkitUser])

  const signedIn = !!authkitUser || serverRole !== null
  const isAdmin = serverRole === 'admin' || serverRole === 'manager'

  function doSignIn() {
    if (signIn) signIn()
    else window.location.assign('/auth/login')
  }
  function doSignUp() {
    window.location.assign('/auth/login?signup=1')
  }

  return (
    <header className="bg-ink text-white h-14 flex items-center px-4 gap-4 sticky top-0 z-30">
      <Link to="/" className="font-heading uppercase font-bold tracking-[0.18em] text-sm flex items-center gap-1.5 hover:opacity-80">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        EVRST<span className="text-gold">.</span>PRINTS
      </Link>
      {title && <span className="text-xs text-white/45 hidden md:inline-block">/ {title}</span>}
      <div className="ml-auto flex items-center gap-3">
        {right}
        {signedIn ? (
          <>
            {isAdmin && (
              <Link to="/admin" className="btn-ghost-dark text-gold hover:text-white" title="Admin shell">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="mr-1.5">
                  <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
                Admin
              </Link>
            )}
            <Link to="/profile" className="btn-ghost-dark" title="Manage profile, orders + notes">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="mr-1.5">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
              Profile
            </Link>
            <button onClick={() => signOut()} className="btn-ghost-dark">Sign out</button>
          </>
        ) : (
          <>
            <button onClick={doSignIn} className="btn-ghost-dark">Sign in</button>
            <button onClick={doSignUp} className="btn-ghost-dark text-gold hover:text-white">Sign up</button>
          </>
        )}
      </div>
    </header>
  )
}
